/** Growth beyond this many times is shown as a cap rather than a five-digit percentage. */
export const TREND_CAP_MULTIPLE = 10;

export type TrendKind =
  | { kind: 'none' }
  /** The channel has no data for the previous period: it started measuring after that period began. */
  | { kind: 'new'; measuringSince: string }
  /** Growth from €0, or more than TREND_CAP_MULTIPLE times. */
  | { kind: 'capped'; pct: number | null; prior: number | null }
  | { kind: 'pct'; pct: number; prior: number | null };

/**
 * One trend rule for every period. "new" means one thing only: the channel
 * has no previous-period data, because it started measuring after the
 * previous period began. A channel measured through the previous period that
 * generated €0 there grew from zero (shown capped, ">10×"), and a channel with
 * a previous value shows its percentage even when it started measuring
 * recently: CRM lifecycle (since Jul 2026) read "new" on 90 days while the
 * API said +129.9%, and Website activity (since May 2023) read "new" on 12
 * months but ">10×" on 6.
 */
export function trendKind(input: {
  pct: number | null;
  current: number;
  /** Generated in the previous period; null on an older API build that does not send it. */
  previous: number | null;
  measuringSince: string | null;
  days: number;
  now?: number;
}): TrendKind {
  const { pct, current, previous, measuringSince, days } = input;
  const now = input.now ?? Date.now();
  const prior = previous ?? (pct !== null && pct > -100 ? current / (1 + pct / 100) : null);
  const priorStart = now - 2 * days * 86_400_000;
  const noPriorData = measuringSince !== null && new Date(measuringSince).getTime() > priorStart;
  if (current > 0 && noPriorData && !prior) return { kind: 'new', measuringSince: measuringSince! };
  if (current > 0 && prior === 0) return { kind: 'capped', pct: null, prior: 0 };
  if (pct === null) return { kind: 'none' };
  if (pct / 100 + 1 > TREND_CAP_MULTIPLE) return { kind: 'capped', pct, prior };
  return { kind: 'pct', pct, prior };
}
